import { useEffect, useState } from "react";
import type {
  CheckoutPaymentStatusResponseType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import { useCheckoutPaymentStatus } from "../../../hooks/api/v2/checkout/useCheckoutPaymentStatus";
import { useUnifiedCheckoutBasket } from "../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useUnifiedCheckout } from "../UnifiedCheckoutProvider.shared";

export type UnifiedCheckoutOrderPaymentState = {
  status: CheckoutPaymentStatusResponseType["status"] | "succeeded";
  isAsyncPayment: boolean;
  isFailed: boolean;
  isPending: boolean;
  isError: boolean;
  failureReason: string;
};

export const useUnifiedCheckoutOrderPaymentStatus = (
  order: OrderGetResponseType,
): UnifiedCheckoutOrderPaymentState => {
  const { sdBasket } = useUnifiedCheckout();
  const { refetch } = useUnifiedCheckoutBasket(sdBasket.uuid);
  const isAsyncPayment =
    order.paymentType === "checkout" || order.paymentType === "sellarCredit";
  const [pollingStartedAt] = useState(() => Date.now());
  const paymentQuery = useCheckoutPaymentStatus(
    { orderId: order.id },
    {
      enabled: isAsyncPayment,
      staleTime: 0,
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return (status === "pending" || status === "not_started") &&
          Date.now() - pollingStartedAt < 10 * 60 * 1000
          ? 5000
          : false;
      },
    },
  );
  const basketStatus = sdBasket.failedOrderIds?.includes(order.id)
    ? "failed"
    : sdBasket.paidOrderIds?.includes(order.id)
      ? "succeeded"
      : "pending";
  const status = !isAsyncPayment
    ? "succeeded"
    : (paymentQuery.data?.status ?? basketStatus);
  const basketStatusMatches =
    status === "succeeded"
      ? sdBasket.paidOrderIds?.includes(order.id)
      : status === "failed"
        ? sdBasket.failedOrderIds?.includes(order.id)
        : true;

  useEffect(() => {
    if (
      isAsyncPayment &&
      (status === "failed" || status === "succeeded") &&
      !basketStatusMatches
    ) {
      void refetch();
    }
  }, [basketStatusMatches, isAsyncPayment, refetch, status]);

  return {
    status,
    isAsyncPayment,
    isFailed: status === "failed",
    isPending: status !== "succeeded",
    isError: paymentQuery.isError,
    failureReason:
      paymentQuery.data?.failureMessage ??
      (order.paymentType === "checkout"
        ? "Your card was declined"
        : "Your payment was declined"),
  };
};
